/**
 * Configurator data helpers.
 * Pure functions for querying configurator data by category key and item id.
 */

/**
 * Find a category node by its key (e.g. "door", "windows", "vent").
 *
 * @param {Array} data - The root configurator data array.
 * @param {string} key - The category key to search for.
 * @returns {Object|null} The category object if found, otherwise null.
 */
export function getCategoryByKey(data, key) {
	if (!Array.isArray(data) || !key) return null;
	return data.find((category) => category.key === key) || null;
}

/**
 * Get a single item by category key and item id.
 * Returns the first match only — suitable when ids are unique.
 *
 * @param {Array} data - The root configurator data array.
 * @param {string} key - The category key (e.g. "door").
 * @param {string} id - The unique item id to look up.
 * @returns {Object|null} The matched item, or null if not found.
 */
export function getItemById(data, key, id) {
	if (!Array.isArray(data) || !key || !id) return null;

	const category = getCategoryByKey(data, key);

	// Guard: category must exist and contain an array of items.
	if (!category || !Array.isArray(category.data)) return null;

	return category.data.find((item) => item.id === id) || null;
}

/**
 * Get all items matching a category key and item id.
 * Useful when duplicate ids exist in the same category.
 *
 * @param {Array} data - The root configurator data array.
 * @param {string} key - The category key (e.g. "door").
 * @param {string} id - The item id to look up.
 * @returns {Array} Array of matched items (empty if none found).
 */
export function getItemsById(data, key, id) {
	if (!Array.isArray(data) || !key || !id) return [];

	const category = getCategoryByKey(data, key);

	// Guard: category must exist and contain an array of items.
	if (!category || !Array.isArray(category.data)) return [];

	return category.data.filter((item) => item.id === id);
}

/**
 * Search for an item by id across every category.
 * Useful when the category key is unknown.
 *
 * @param {Array} data - The root configurator data array.
 * @param {string} id - The item id to look up.
 * @returns {Object|null} The first matched item, or null if not found.
 */
export function findItemAnywhere(data, id) {
	if (!Array.isArray(data) || !id) return null;

	for (const category of data) {
		if (!Array.isArray(category.data)) continue;

		const found = category.data.find((item) => item.id === id);
		if (found) return found;
	}

	return null;
}

/**
 * Build the available door-transom options for a door catalog record.
 *
 * A door record stores transoms per position as
 * `{ transoms: Array<{ ssb_configurator_component_uuid }>, position_name }`
 * under `transom_in_door` and `transom_above`.
 *
 * @param {Object} doorData - Door catalog item (componentCatalog `door` entry).
 * @returns {Array<{uuid: string, position: string, positionName: string}>} Available options.
 */
export function getDoorTransomOptions(doorData) {
	const options = [];
	const positions = ['transom_in_door', 'transom_above'];

	if (!doorData || typeof doorData !== 'object') return options;

	positions.forEach((position) => {
		const positionData = doorData[position];
		const transoms = Array.isArray(positionData?.transoms) ? positionData.transoms : [];
		const positionName = String(positionData?.position_name || '').trim();

		transoms.forEach((item) => {
			const uuid = item?.ssb_configurator_component_uuid;
			if (uuid === undefined || uuid === null || '' === String(uuid)) return;
			options.push({ uuid: String(uuid), position, positionName });
		});
	});

	return options;
}

/**
 * Whether a door allows "None" for its transom add-on.
 *
 * Only an explicit false/0/'0'/'false' disables None; a missing value allows it.
 *
 * @param {Object} doorData - Door catalog item (componentCatalog `door` entry).
 * @returns {boolean} True when "None" is allowed.
 */
export function doorTransomAllowNone(doorData) {
	return (
		doorData?.transom_allow_none !== false &&
		doorData?.transom_allow_none !== 0 &&
		doorData?.transom_allow_none !== '0' &&
		doorData?.transom_allow_none !== 'false'
	);
}

/**
 * Default export — groups all helpers into a single object.
 * Allows both `import helpers from ...` and `import { getItemById } from ...`.
 */
export default {
	getCategoryByKey,
	getItemById,
	getItemsById,
	findItemAnywhere,
	getDoorTransomOptions,
	doorTransomAllowNone,
};
